import { useState, useEffect, useRef, useCallback, useMemo, type ReactNode } from "react";
import { Play, Loader2 } from "lucide-react";
import { useEvent } from "../hooks/useEvent";
import { useResponses } from "../hooks/useResponses";
import { useClusters } from "../hooks/useClusters";
import { useSurveyAI } from "../hooks/useSurveyAI";
import { useEventTools } from "../hooks/useEventTools";
import { useStageCarousel } from "../hooks/useStageCarousel";
import { useLivePlacement } from "../hooks/useLivePlacement";
import { getEventByJoinCode, advanceSurveyQuestion, setQuestionOpen, updateEvent, setStageView } from "../lib/api";
import { useCurrentEventRole } from "../hooks/useCurrentEventRole";
import { can } from "../lib/permissions";
import { isVisibleResponse } from "../lib/responses";
import { supabase } from "../lib/supabase";
import ResponseTicker from "../components/ResponseTicker";
import FlightLayer from "../components/FlightLayer";
import InsightTicker from "../components/InsightTicker";
import IncomingLane from "../components/IncomingLane";
import CarouselProgress from "../components/CarouselProgress";
import QRCode from "../components/QRCode";
import type { AIInsight, Event, Response } from "../lib/types";
import StageResultsPanel from "../components/Stage/StageResultsPanel";
import { resolveView, viewsFor, type StageViewDef, type StageViewKey } from "../lib/stageViews";
import { useSegmentStats } from "../hooks/useSegmentStats";
import StageAIAnchor from "../components/Stage/StageAIAnchor";
import StageInsightOverlay from "../components/Stage/StageInsightOverlay";
import { useInsightTickerSlots } from "../hooks/useInsightTickerSlots";
import StageViewSwitcher from "../components/Stage/StageViewSwitcher";

interface Flight {
  id: string;
  text: string;
  from: { x: number; y: number };
  to: { x: number; y: number };
  startedAt: number;
  /** Target cluster id — used to pulse the cluster card when the flight lands. */
  clusterId: string;
}

/**
 * Live placement orchestration: tracks responses whose cluster_id transitions
 * (null → uuid, or cluster_id → different uuid) and emits flight animations
 * from the ticker card position to the target cluster card position.
 *
 * The two ref maps (tickerCardRefs / clusterCardRefs) are populated by the
 * children via the `registerCardRef` / `registerClusterRef` callbacks.
 *
 * Uncategorized responses (cluster_id still null) do not get a flight here —
 * the Außerhalb zone simply appears once at least one exists.
 *
 * Resets cleanly when questionId changes (so an in-flight question switch
 * doesn't leak stale flights or stale prev-placement state into the next
 * question).
 */
function useStageFlightAnimations(
  questionId: string | null,
  visibleResponses: Response[],
) {
  const tickerCardRefs = useRef<Map<string, HTMLElement>>(new Map());
  const clusterCardRefs = useRef<Map<string, HTMLElement>>(new Map());
  // Cache of the last-known ticker card position for each response. Populated
  // while the ticker card is mounted; falls back here when the carousel rotates
  // into the cluster phase and unmounts the ticker (so flights still have a
  // sensible `from` rect). Captured on the registerTickerCardRef callback path
  // (mount + unmount) so any visible card always leaves a fresh trail.
  const lastTickerRectByResponseId = useRef<Map<string, DOMRect>>(new Map());
  // Stage Calm: once any cluster exists the ResponseTicker is no longer
  // mounted, so per-response ticker rects don't exist. The IncomingLane on
  // the right sidebar is the on-screen home of "incoming" responses in that
  // mode, so we use its bounding rect as the flight origin when no
  // per-response rect is available.
  const laneElementRef = useRef<HTMLElement | null>(null);
  const [flights, setFlights] = useState<Flight[]>([]);
  const prevPlacedRef = useRef<Map<string, string | null>>(new Map());

  // Per-placement landing pulse: when FlightLayer reports a flight has just
  // touched its target cluster card, surface that cluster id for 300ms so
  // ClusterDisplay can apply a 1.04 → 1.0 scale on the single receiving card.
  // Declared up here so the question-reset effect below can reach them
  // without relying on JS hoisting of `let` bindings (the reset effect needs
  // to clear the pulse + cancel the timeout when switching questions).
  const [pulseClusterId, setPulseClusterId] = useState<string | null>(null);
  const pulseTimeoutRef = useRef<number | null>(null);

  // Reset all in-flight state when the question changes — animations from a
  // previous question must not bleed into the next.
  useEffect(() => {
    prevPlacedRef.current = new Map();
    setFlights([]);
    tickerCardRefs.current = new Map();
    clusterCardRefs.current = new Map();
    lastTickerRectByResponseId.current = new Map();
    // Note: do NOT null laneElementRef here. The IncomingLane is the same
    // DOM element across question changes — it doesn't unmount when
    // questionId changes, so its ref callback is never re-invoked. Wiping
    // the ref on every question change leaves it permanently null and the
    // flight `from` rect resolution fails with no flight origin. The lane
    // element legitimately outlives questions; the ref stays valid.
    setPulseClusterId(null);
    if (pulseTimeoutRef.current != null) {
      window.clearTimeout(pulseTimeoutRef.current);
      pulseTimeoutRef.current = null;
    }
  }, [questionId]);

  const placementSignature = visibleResponses
    .map((r) => `${r.id}:${r.cluster_id ?? ""}`)
    .join("|");

  useEffect(() => {
    const now = Date.now();
    const additions: Flight[] = [];
    for (const r of visibleResponses) {
      // Per-response hydration: the first time we see a response (not yet in
      // prevPlacedRef), seed its current cluster_id without firing a flight.
      // This handles two cases:
      //   1. Initial mount with many already-placed responses.
      //   2. Question rotation where useResponses streams in old responses
      //      asynchronously — without per-response hydration, each response
      //      arriving with cluster_id != null would be treated as a fresh
      //      transition and fire a phantom flight for an old placement.
      // Real transitions only fire for responses we've seen at least once
      // with a different cluster_id (or null).
      if (!prevPlacedRef.current.has(r.id)) {
        prevPlacedRef.current.set(r.id, r.cluster_id);
        continue;
      }
      const prev = prevPlacedRef.current.get(r.id) ?? null;
      const cur = r.cluster_id;
      // Freshness filter: only animate placements of responses that were
      // submitted recently. Backfilled placements (old responses that get a
      // cluster_id assigned because a re-cluster ran or because the place
      // queue finally reached them hours later) are not "moments the room
      // watched happen" — they shouldn't fly. 30 s is generous enough to
      // cover gpt-4o-mini placement latency under load while still
      // excluding old rows that get re-placed via cluster-path reset.
      const FRESHNESS_MS = 30_000;
      const submittedMs = r.submitted_at ? Date.parse(r.submitted_at) : 0;
      const isFresh = submittedMs > 0 && now - submittedMs < FRESHNESS_MS;
      if (prev !== cur && cur != null && isFresh) {
        const fromEl = tickerCardRefs.current.get(r.id);
        const toEl = clusterCardRefs.current.get(cur);
        // `from` rect resolution order:
        //   1. Live ticker card (carousel responses-phase, pre-Stage-Calm).
        //   2. Cached last-known ticker rect (ticker just unmounted, response
        //      was visible there moments ago).
        //   3. IncomingLane element (Stage Calm steady state — ticker never
        //      mounts once any cluster exists, but the lane on the right
        //      sidebar shows incoming responses). Without this third tier,
        //      no flight ever queues in Stage Calm and the landing pulse
        //      never fires.
        let fr: DOMRect | undefined;
        if (fromEl) {
          fr = fromEl.getBoundingClientRect();
          lastTickerRectByResponseId.current.set(r.id, fr);
        } else {
          fr = lastTickerRectByResponseId.current.get(r.id);
          if (!fr && laneElementRef.current) {
            fr = laneElementRef.current.getBoundingClientRect();
          }
        }
        if (fr && toEl) {
          const tr = toEl.getBoundingClientRect();
          additions.push({
            id: `${r.id}-${now}`,
            text: r.text.length > 80 ? r.text.slice(0, 80) + "…" : r.text,
            from: { x: fr.left + fr.width / 2, y: fr.top + fr.height / 2 },
            to: { x: tr.left + tr.width / 2, y: tr.top + tr.height / 2 },
            startedAt: now,
            clusterId: cur,
          });
        }
      }
      prevPlacedRef.current.set(r.id, cur);
    }
    if (additions.length > 0) {
      setFlights((prev) => [...prev, ...additions]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placementSignature]);

  const handleFlightDone = useCallback((id: string) => {
    setFlights((prev) => prev.filter((f) => f.id !== id));
  }, []);

  const handleFlightLand = useCallback((clusterId: string) => {
    if (pulseTimeoutRef.current != null) {
      window.clearTimeout(pulseTimeoutRef.current);
    }
    setPulseClusterId(clusterId);
    // Match the cluster-land keyframe duration (800 ms) so the className stays
    // on the card for the entire halo + scale animation. Dropping it earlier
    // would cut the animation off mid-flash.
    pulseTimeoutRef.current = window.setTimeout(() => {
      setPulseClusterId(null);
      pulseTimeoutRef.current = null;
    }, 800);
  }, []);

  // Tidy the timeout if the hook unmounts mid-pulse.
  useEffect(() => {
    return () => {
      if (pulseTimeoutRef.current != null) {
        window.clearTimeout(pulseTimeoutRef.current);
      }
    };
  }, []);

  const registerTickerCardRef = useCallback(
    (id: string, el: HTMLElement | null) => {
      if (el) {
        tickerCardRefs.current.set(id, el);
        // Snapshot immediately so a subsequent placement during the same
        // phase finds a fresh rect even if React hasn't fired the next
        // effect yet.
        lastTickerRectByResponseId.current.set(id, el.getBoundingClientRect());
      } else {
        // On unmount (e.g. carousel phase change to `clusters`) freeze the
        // last-known rect so future placements can still animate from it.
        const prev = tickerCardRefs.current.get(id);
        if (prev) {
          lastTickerRectByResponseId.current.set(id, prev.getBoundingClientRect());
        }
        tickerCardRefs.current.delete(id);
      }
    },
    [],
  );

  const registerClusterCardRef = useCallback(
    (id: string, el: HTMLElement | null) => {
      if (el) clusterCardRefs.current.set(id, el);
      else clusterCardRefs.current.delete(id);
    },
    [],
  );

  // Stage Calm fallback origin: the IncomingLane on the right sidebar.
  // See `laneElementRef` declaration above.
  const registerLaneRef = useCallback((el: HTMLElement | null) => {
    laneElementRef.current = el;
  }, []);

  return {
    flights,
    handleFlightDone,
    handleFlightLand,
    pulseClusterId,
    registerLaneRef,
    registerTickerCardRef,
    registerClusterCardRef,
  };
}

/**
 * Tracks new cluster_runs and surfaces a 650ms "Cluster werden neu gelesen…"
 * banner for any subsequent run (not the initial one). The cluster column is
 * faded to 30% opacity while the banner is up. Plain crossfade — no morph.
 */
function useReclusterBanner(clusters: { run_id: string }[]) {
  const [rebuildBanner, setRebuildBanner] = useState(false);
  const prevRunIdRef = useRef<string | null>(null);

  // Detect new run_id and raise the banner. Ref-only — no timer cleanup here,
  // so subsequent realtime updates (e.g. cluster rows arriving one-by-one)
  // don't cancel the clear-timer.
  useEffect(() => {
    const latestRunId = clusters[0]?.run_id ?? null;
    if (latestRunId && latestRunId !== prevRunIdRef.current) {
      const isInitial = prevRunIdRef.current === null;
      prevRunIdRef.current = latestRunId;
      if (!isInitial) setRebuildBanner(true);
    }
  }, [clusters]);

  // Independent clear-timer keyed on banner state. The cleanup only fires when
  // the banner itself toggles, never on unrelated dep churn.
  useEffect(() => {
    if (!rebuildBanner) return;
    const t = setTimeout(() => setRebuildBanner(false), 650);
    return () => clearTimeout(t);
  }, [rebuildBanner]);

  return rebuildBanner;
}

/** Renders the "Cluster werden neu gelesen…" shimmer pill. */
function ReclusterBanner() {
  // Positioned below the StageHeader band (~64px) + carousel progress strip.
  // Using `top-24` (6rem / 96px) so the pill floats over the cluster column
  // rather than colliding with the header / question-progress dots.
  return (
    <div className="absolute top-24 left-1/2 -translate-x-1/2 z-40 rounded-pill bg-rc-paper/15 text-rc-paper/85 px-4 py-1.5 text-[12px] animate-shimmer">
      Cluster werden neu gelesen…
    </div>
  );
}

/**
 * Stage AI News Ticker: realtime trigger for the BREAKING flag on a tool's
 * ticker slot.
 *
 * Subscribes to ai_insights INSERT *and* UPDATE for the question. The edge
 * function deduplicates ai_insights rows per (question_id, tool_id) — the
 * first run of a tool INSERTs, every subsequent rerun UPDATEs the same row.
 * Without the UPDATE branch, only the very first instance of each tool ever
 * fires a BREAKING flag.
 *
 * Rows with `tool_id === null` (legacy untagged insights) are ignored — the
 * ticker is per-tool, so they have no slot to live in. Rows with
 * `is_displayed === false` are also ignored.
 *
 * On `consume()`, the flag resets (called by the ticker after one full loop
 * pass). On question change, resets immediately.
 */
function useBreakingInsight(questionId: string | null): {
  breakingToolId: string | null;
  consume: () => void;
} {
  const [breakingToolId, setBreakingToolId] = useState<string | null>(null);

  useEffect(() => {
    setBreakingToolId(null);
  }, [questionId]);

  useEffect(() => {
    if (!questionId) return;
    const handleRow = (payload: { new: unknown }) => {
      const row = payload.new as AIInsight;
      if (!row.is_displayed) return;
      if (!row.tool_id) return;
      setBreakingToolId(row.tool_id);
    };
    const channel = supabase
      .channel(`stage-breaking-${questionId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "ai_insights",
          filter: `question_id=eq.${questionId}`,
        },
        handleRow,
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "ai_insights",
          filter: `question_id=eq.${questionId}`,
        },
        handleRow,
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [questionId]);

  const consume = useCallback(() => setBreakingToolId(null), []);

  return { breakingToolId, consume };
}

interface Props {
  joinCode: string;
}

export default function StageView({ joinCode }: Props) {
  const [event, setEvent] = useState<Event | null>(null);
  const [eventLoading, setEventLoading] = useState(true);

  useEffect(() => {
    getEventByJoinCode(joinCode).then((e) => {
      setEvent(e);
      setEventLoading(false);
    });
  }, [joinCode]);

  if (eventLoading) {
    return <StageLoading />;
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-rc-ink text-rc-paper flex flex-col items-center justify-center text-center px-10 gap-4">
        <p className="text-[11px] font-semibold tracking-[0.28em] text-rc-stone uppercase">READYCAL</p>
        <h1 className="text-3xl font-bold tracking-[-0.02em]">Event nicht gefunden.</h1>
        <p className="text-rc-stone">
          Join-Code <span className="font-mono font-semibold text-rc-lime/70">{joinCode}</span> nicht gefunden.
        </p>
      </div>
    );
  }

  if (event.event_mode === "survey") {
    return <SurveyStageSession event={event} />;
  }

  return <StageSession event={event} />;
}

function StageLoading() {
  return (
    <div className="min-h-screen bg-rc-ink flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-rc-paper/10 border-t-rc-berry rounded-full animate-spin" />
    </div>
  );
}

const SHORT_JOIN_URL = "reasonz.de/join";

function truncate(s: string, max = 36) {
  if (s.length <= max) return s;
  return s.slice(0, max - 1).trimEnd() + "…";
}

// ─── Survey Stage ───────────────────────────────────────────────────────────

function SurveyStageSession({ event: initialEvent }: { event: Event }) {
  const { event, questions, loading } = useEvent(initialEvent.id);
  const [accessToken, setAccessToken] = useState("");
  const [transitioning, setTransitioning] = useState(false);
  const progressStartRef = useRef<number>(Date.now());
  const [progressPct, setProgressPct] = useState(0);

  const liveEvent = event ?? initialEvent;
  const displayedIdx = liveEvent.displayed_question_index ?? 0;
  const displayedQuestion = questions[displayedIdx] ?? questions[0] ?? null;

  // Refs so the interval callback always reads the latest values
  const displayedIdxRef = useRef(displayedIdx);
  const questionsLengthRef = useRef(questions.length);
  const eventIdRef = useRef(liveEvent.id);
  displayedIdxRef.current = displayedIdx;
  questionsLengthRef.current = questions.length;
  eventIdRef.current = liveEvent.id;

  const { responses } = useResponses(displayedQuestion?.id ?? null);
  const { clusters, insights } = useClusters(displayedQuestion?.id ?? null);
  const { tools } = useEventTools(liveEvent.id);

  // Owner/co-admin facilitators can jump the displayed question by clicking
  // the progress dots (same gate as the facilitated stage controls).
  const { role } = useCurrentEventRole(liveEvent.id);
  const canControl = can(role, "open_close_question");
  const [switching, setSwitching] = useState(false);

  const jumpToQuestion = async (i: number) => {
    if (switching || i === displayedIdx || i < 0 || i >= questions.length) return;
    setSwitching(true);
    try {
      await updateEvent(liveEvent.id, { displayed_question_index: i });
    } finally {
      setSwitching(false);
    }
  };

  useLivePlacement(displayedQuestion?.id ?? null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setAccessToken(data.session?.access_token ?? "");
    });
  }, []);

  useSurveyAI({
    question: displayedQuestion,
    responses,
    tools,
    accessToken,
    eventId: liveEvent.id,
    reclusterThreshold: liveEvent.recluster_threshold,
    insightTriggerThreshold: liveEvent.insight_trigger_threshold,
  });

  // Reset progress bar when the displayed question changes
  useEffect(() => {
    progressStartRef.current = Date.now();
    setProgressPct(0);
  }, [displayedIdx]);

  // Auto-rotation timer
  useEffect(() => {
    if (!liveEvent.rotation_interval_seconds) return;

    const intervalMs = liveEvent.rotation_interval_seconds * 1000;
    progressStartRef.current = Date.now();

    const rotationTimer = setInterval(() => {
      if (questionsLengthRef.current <= 1 || document.visibilityState !== "visible") return;
      setTransitioning(true);
      advanceSurveyQuestion(eventIdRef.current, displayedIdxRef.current, questionsLengthRef.current)
        .then(() => setTimeout(() => setTransitioning(false), 400));
    }, intervalMs);

    const progressTick = setInterval(() => {
      const elapsed = Date.now() - progressStartRef.current;
      setProgressPct(Math.min((elapsed / intervalMs) * 100, 100));
    }, 100);

    return () => {
      clearInterval(rotationTimer);
      clearInterval(progressTick);
    };
  }, [liveEvent.rotation_interval_seconds]);

  const visibleResponses = useMemo(
    () => responses.filter((r) => isVisibleResponse(r)),
    [responses],
  );
  const participantUrl = `${window.location.origin}/join/${liveEvent.join_code}`;
  const total = questions.length;

  const responsesByCluster = useMemo(() => {
    const map = new Map<string, Response[]>();
    for (const r of visibleResponses) {
      if (r.cluster_id == null) continue;
      const list = map.get(r.cluster_id) ?? [];
      list.push(r);
      map.set(r.cluster_id, list);
    }
    return map;
  }, [visibleResponses]);

  const segmentQuestion = useMemo(
    () => questions.find((q) => q.id === liveEvent.segment_question_id) ?? null,
    [questions, liveEvent.segment_question_id],
  );
  const canSwitch = can(role, "switch_stage_view");
  const views = useMemo(
    () => (displayedQuestion ? viewsFor(displayedQuestion, liveEvent, { hasClusters: clusters.length > 0 }) : []),
    [displayedQuestion, liveEvent, clusters.length],
  );
  const [pendingView, setPendingView] = useState<StageViewKey | null>(null);
  const [switchingView, setSwitchingView] = useState(false);
  useEffect(() => { setPendingView(null); }, [displayedQuestion?.stage_view, displayedQuestion?.id]);
  const selectView = useCallback(async (key: StageViewKey) => {
    if (!displayedQuestion || switchingView) return;
    setPendingView(key);
    setSwitchingView(true);
    try { await setStageView(displayedQuestion.id, key); }
    catch { setPendingView(null); }
    finally { setSwitchingView(false); }
  }, [displayedQuestion, switchingView]);
  useStageViewKeyboard(canSwitch && !!displayedQuestion, views, selectView);

  const storedView = displayedQuestion ? resolveView(displayedQuestion, liveEvent, { hasClusters: clusters.length > 0 }) : null;
  const view = pendingView && views.some((v) => v.key === pendingView)
    ? views.find((v) => v.key === pendingView)!
    : storedView;
  const isOpenText = displayedQuestion?.question_type === "open_text";
  const stats = useSegmentStats(displayedQuestion?.id ?? null, responses, !!displayedQuestion && !isOpenText);

  // Zitate stepping lives here (local, newest-first index); keyboard arrows and
  // the console remote both land on these handlers while the quotes view is up.
  const [quoteIndex, setQuoteIndex] = useState(0);
  useEffect(() => { setQuoteIndex(0); }, [displayedQuestion?.id]);
  const quotesActive = view?.key === "quotes";
  const stepQuote = useCallback((dir: 1 | -1) => setQuoteIndex((i) => i + dir), []);

  const {
    flights,
    handleFlightDone,
    handleFlightLand,
    pulseClusterId,
    registerLaneRef,
    registerTickerCardRef,
    registerClusterCardRef,
  } = useStageFlightAnimations(displayedQuestion?.id ?? null, visibleResponses);

  const rebuildBanner = useReclusterBanner(clusters);

  const carousel = useStageCarousel({
    eventId: liveEvent.id,
    questionId: displayedQuestion?.id ?? null,
    clusterCount: clusters.length,
    dbPaused: liveEvent.carousel_paused,
    onNext: quotesActive ? () => stepQuote(1) : undefined,
    onPrev: quotesActive ? () => stepQuote(-1) : undefined,
  });

  useStageCarouselKeyboard(
    quotesActive ? () => stepQuote(1) : carousel.advance,
    quotesActive ? () => stepQuote(-1) : carousel.retreat,
  );

  const { breakingToolId, consume: consumeBreaking } = useBreakingInsight(
    displayedQuestion?.id ?? null,
  );

  const [insightOverlayOpen, setInsightOverlayOpen] = useState(false);
  const tickerSlots = useInsightTickerSlots(insights, tools);

  if (loading) return <StageLoading />;

  if (!displayedQuestion) {
    return <SurveyWaitingScreen event={liveEvent} participantUrl={participantUrl} />;
  }

  const currentView = view!;
  const showWall = isOpenText && currentView.key === "themes" && clusters.length === 0;
  const surveySwapKey = `${displayedQuestion.id}|${currentView.key}|${showWall ? "wall" : "panel"}`;
  const showLane = !liveEvent.responses_paused && isOpenText && currentView.key === "themes" && clusters.length > 0;

  return (
    <div className="relative h-screen overflow-hidden bg-rc-ink text-rc-paper flex flex-col font-sans antialiased">
      <StageHeader
        eventTitle={liveEvent.title}
        statusKind="survey"
        progressDots={
          total > 1 ? (
            <div className="flex items-center gap-4">
              <span className="text-[11px] font-semibold tracking-[0.28em] text-rc-paper/70 uppercase whitespace-nowrap flex-shrink-0">
                Frage {displayedIdx + 1} / {total}
              </span>
              <div className="flex items-center gap-2">
                {questions.map((q, i) => {
                  const dotCls = `h-1 ${total > 8 ? "w-8" : "w-16"} rounded-full transition-colors duration-500 ${
                    i === displayedIdx ? "bg-rc-lime" : i < displayedIdx ? "bg-rc-berry" : "bg-rc-paper/10"
                  }`;
                  if (!canControl) return <span key={i} className={dotCls} />;
                  return (
                    <button
                      key={i}
                      type="button"
                      onClick={() => jumpToQuestion(i)}
                      disabled={switching}
                      title={q.text}
                      aria-label={`Frage ${i + 1} anzeigen`}
                      className={`${dotCls} hover:bg-rc-lime/70 cursor-pointer disabled:cursor-wait active:scale-y-[2] transition-transform`}
                    />
                  );
                })}
              </div>
            </div>
          ) : null
        }
      />

      {liveEvent.rotation_interval_seconds && total > 1 && (
        <div className="h-0.5 bg-rc-paper/5 flex-shrink-0">
          <div
            className="h-full bg-rc-lime/60 transition-none"
            style={{ width: `${progressPct}%` }}
          />
        </div>
      )}

      <CarouselProgress
        phases={carousel.phases}
        currentIdx={carousel.phaseIdx}
        progress={carousel.progress}
        isPaused={carousel.isPaused}
      />

      {rebuildBanner && <ReclusterBanner />}

      <div
        className={`flex flex-1 min-h-0 px-12 pb-8 gap-10 overflow-hidden transition-opacity duration-300 ${
          transitioning ? "opacity-0" : "opacity-100"
        }`}
      >
        <div className="relative flex flex-col flex-1 min-h-0 min-w-0">
          <div className="flex-shrink-0 mb-4 sm:mb-5">
            <h1
              className="text-rc-paper font-bold leading-[1.05] tracking-[-0.03em] text-balance mt-4"
              style={{ fontSize: "clamp(1.5rem, 2.8vw, 3.1rem)", maxWidth: "32ch" }}
            >
              {displayedQuestion.text}
            </h1>
            {canSwitch && view && views.length > 1 && (
              <StageViewSwitcher views={views} activeKey={view.key} onSelect={(k) => void selectView(k)} busy={switchingView} />
            )}
            {displayedQuestion.hint && (
              <p
                className="text-rc-stone mt-5 leading-[1.5]"
                style={{ fontSize: "clamp(0.95rem, 1.35vw, 1.2rem)", maxWidth: "70ch" }}
              >
                {displayedQuestion.hint}
              </p>
            )}
          </div>

          <div
            key={surveySwapKey}
            className="flex-1 min-h-0 overflow-hidden border-t border-rc-paper/8 pt-6 animate-fade-up"
          >
            {liveEvent.responses_paused ? (
              <StagePausedCard />
            ) : showWall ? (
              <ResponseTicker
                responses={visibleResponses}
                question={displayedQuestion}
                registerCardRef={registerTickerCardRef}
              />
            ) : (
              <StageResultsPanel
                question={displayedQuestion}
                segmentQuestion={segmentQuestion}
                responses={visibleResponses}
                clusters={clusters}
                responsesByCluster={responsesByCluster}
                stats={stats}
                view={currentView}
                quoteIndex={quoteIndex}
                onQuoteStep={canControl ? stepQuote : undefined}
                registerClusterRef={registerClusterCardRef}
                pulseClusterId={pulseClusterId}
                dimmed={rebuildBanner}
                registerCardRef={registerTickerCardRef}
              />
            )}
          </div>
        </div>

        <StageAside
          responseCount={visibleResponses.length}
          participantUrl={participantUrl}
          joinCode={liveEvent.join_code}
          incomingLane={
            showLane ? <IncomingLane registerLaneRef={registerLaneRef} /> : null
          }
        />
      </div>

      <InsightTicker
        insights={insights}
        tools={tools}
        breakingToolId={breakingToolId}
        onBreakingConsumed={consumeBreaking}
        leading={
          <StageAIAnchor
            event={liveEvent}
            question={displayedQuestion}
            responses={visibleResponses}
            tools={tools}
            hasClusters={clusters.length > 0}
          />
        }
        onExpand={() => setInsightOverlayOpen(true)}
      />

      <FlightLayer
        flights={flights}
        onFlightDone={handleFlightDone}
        onFlightLand={handleFlightLand}
      />

      {insightOverlayOpen && (
        <StageInsightOverlay
          slots={tickerSlots}
          questionText={displayedQuestion.text}
          onClose={() => setInsightOverlayOpen(false)}
        />
      )}
    </div>
  );
}

function SurveyWaitingScreen({ event, participantUrl }: { event: Event; participantUrl: string }) {
  return (
    <WaitingShell
      event={event}
      participantUrl={participantUrl}
      eyebrow="Survey wird vorbereitet"
      eyebrowAccent="lime"
      footerLine="Antworten erscheinen, sobald die erste Frage live ist"
    />
  );
}

// ─── Facilitated Stage ──────────────────────────────────────────────────────

function StageSession({ event: initialEvent }: { event: Event }) {
  const { event: liveEvent, questions, activeQuestion, loading } = useEvent(initialEvent.id);
  const event = liveEvent ?? initialEvent;
  const { responses } = useResponses(activeQuestion?.id ?? null);
  const { clusters, insights } = useClusters(activeQuestion?.id ?? null);
  const { tools } = useEventTools(event.id);

  // Owner/co-admin facilitators can drive the session from the stage:
  // clickable progress dots + the waiting screen's start button.
  const { role } = useCurrentEventRole(event.id);
  const canControl = can(role, "open_close_question");
  const [switching, setSwitching] = useState(false);

  const switchToQuestion = async (i: number) => {
    const target = questions[i];
    if (!target || switching) return;
    if (activeQuestion?.id === target.id) return;
    setSwitching(true);
    try {
      // Same semantics as the facilitator console: close every other open
      // question, then open the target.
      for (const other of questions) {
        if (other.is_open && other.id !== target.id) await setQuestionOpen(other.id, false);
      }
      await setQuestionOpen(target.id, true);
    } finally {
      setSwitching(false);
    }
  };

  useLivePlacement(activeQuestion?.id ?? null);

  const visibleResponses = useMemo(
    () => responses.filter((r) => isVisibleResponse(r)),
    [responses],
  );
  const participantUrl = `${window.location.origin}/join/${event.join_code}`;
  const total = questions.length;

  const responsesByCluster = useMemo(() => {
    const map = new Map<string, Response[]>();
    for (const r of visibleResponses) {
      if (r.cluster_id == null) continue;
      const list = map.get(r.cluster_id) ?? [];
      list.push(r);
      map.set(r.cluster_id, list);
    }
    return map;
  }, [visibleResponses]);

  const segmentQuestion = useMemo(
    () => questions.find((q) => q.id === event.segment_question_id) ?? null,
    [questions, event.segment_question_id],
  );
  const canSwitch = can(role, "switch_stage_view");
  const views = useMemo(
    () => (activeQuestion ? viewsFor(activeQuestion, event, { hasClusters: clusters.length > 0 }) : []),
    [activeQuestion, event, clusters.length],
  );
  const [pendingView, setPendingView] = useState<StageViewKey | null>(null);
  const [switchingView, setSwitchingView] = useState(false);
  useEffect(() => { setPendingView(null); }, [activeQuestion?.stage_view, activeQuestion?.id]);
  const selectView = useCallback(async (key: StageViewKey) => {
    if (!activeQuestion || switchingView) return;
    setPendingView(key);
    setSwitchingView(true);
    try { await setStageView(activeQuestion.id, key); }
    catch { setPendingView(null); }
    finally { setSwitchingView(false); }
  }, [activeQuestion, switchingView]);
  useStageViewKeyboard(canSwitch && !!activeQuestion, views, selectView);

  const storedView = activeQuestion ? resolveView(activeQuestion, event, { hasClusters: clusters.length > 0 }) : null;
  const view = pendingView && views.some((v) => v.key === pendingView)
    ? views.find((v) => v.key === pendingView)!
    : storedView;
  const isOpenText = activeQuestion?.question_type === "open_text";
  const stats = useSegmentStats(activeQuestion?.id ?? null, responses, !!activeQuestion && !isOpenText);

  // Zitate stepping lives here (local, newest-first index); keyboard arrows and
  // the console remote both land on these handlers while the quotes view is up.
  const [quoteIndex, setQuoteIndex] = useState(0);
  useEffect(() => { setQuoteIndex(0); }, [activeQuestion?.id]);
  const quotesActive = view?.key === "quotes";
  const stepQuote = useCallback((dir: 1 | -1) => setQuoteIndex((i) => i + dir), []);

  const {
    flights,
    handleFlightDone,
    handleFlightLand,
    pulseClusterId,
    registerLaneRef,
    registerTickerCardRef,
    registerClusterCardRef,
  } = useStageFlightAnimations(activeQuestion?.id ?? null, visibleResponses);

  const rebuildBanner = useReclusterBanner(clusters);

  const carousel = useStageCarousel({
    eventId: event.id,
    questionId: activeQuestion?.id ?? null,
    clusterCount: clusters.length,
    dbPaused: event.carousel_paused,
    onNext: quotesActive ? () => stepQuote(1) : undefined,
    onPrev: quotesActive ? () => stepQuote(-1) : undefined,
  });

  useStageCarouselKeyboard(
    quotesActive ? () => stepQuote(1) : carousel.advance,
    quotesActive ? () => stepQuote(-1) : carousel.retreat,
  );

  const { breakingToolId, consume: consumeBreaking } = useBreakingInsight(
    activeQuestion?.id ?? null,
  );

  const [insightOverlayOpen, setInsightOverlayOpen] = useState(false);
  const tickerSlots = useInsightTickerSlots(insights, tools);

  if (loading) return <StageLoading />;

  if (!activeQuestion) {
    // Explicit completion only. "All questions closed" is NOT a finished
    // event — a room tested before doors open looks exactly like that, and
    // the overview would spoil the questions before the session starts.
    if (event?.status === "completed") {
      return <OverviewScreen event={event} questions={questions} />;
    }
    return (
      <WaitingScreen
        event={event}
        participantUrl={participantUrl}
        onStart={canControl && questions.length > 0 ? () => switchToQuestion(0) : undefined}
      />
    );
  }

  const currentIdx = activeQuestion.order_index;
  const currentView = view!;
  const showWall = isOpenText && currentView.key === "themes" && clusters.length === 0;
  const facilitatedSwapKey = `${activeQuestion.id}|${currentView.key}|${showWall ? "wall" : "panel"}`;
  const showLane = !event.responses_paused && isOpenText && currentView.key === "themes" && clusters.length > 0;

  return (
    <div className="relative h-screen overflow-hidden bg-rc-ink text-rc-paper flex flex-col font-sans antialiased">
      <StageHeader
        eventTitle={event.title}
        statusKind="live"
        progressDots={
          total > 0 ? (
            <div className="flex items-center gap-4">
              <span className="text-[11px] font-semibold tracking-[0.28em] text-rc-paper/70 uppercase whitespace-nowrap flex-shrink-0">
                Frage {currentIdx} / {total}
              </span>
              <div className="flex items-center gap-2">
                {Array.from({ length: total }).map((_, i) => {
                  const n = i + 1;
                  const filled = n <= currentIdx;
                  const dotCls = `h-1 ${total > 8 ? "w-8" : "w-16"} rounded-full transition-colors duration-500 ${
                    filled ? "bg-rc-berry" : "bg-rc-paper/10"
                  }`;
                  if (!canControl) return <span key={n} className={dotCls} />;
                  return (
                    <button
                      key={n}
                      type="button"
                      onClick={() => switchToQuestion(i)}
                      disabled={switching}
                      title={questions[i]?.text}
                      aria-label={`Frage ${n} aktivieren`}
                      className={`${dotCls} hover:bg-rc-berry/70 cursor-pointer disabled:cursor-wait active:scale-y-[2] transition-transform`}
                    />
                  );
                })}
              </div>
            </div>
          ) : null
        }
      />

      <CarouselProgress
        phases={carousel.phases}
        currentIdx={carousel.phaseIdx}
        progress={carousel.progress}
        isPaused={carousel.isPaused}
      />

      {rebuildBanner && <ReclusterBanner />}

      <div className="flex flex-1 min-h-0 px-12 pb-8 gap-10 overflow-hidden">
        <div className="relative flex flex-col flex-1 min-h-0 min-w-0">
          <div className="flex-shrink-0 mb-4 sm:mb-5">
            <h1
              className="text-rc-paper font-bold leading-[1.05] tracking-[-0.03em] text-balance mt-4"
              style={{ fontSize: "clamp(1.5rem, 2.8vw, 3.1rem)", maxWidth: "32ch" }}
            >
              {activeQuestion.text}
            </h1>
            {canSwitch && view && views.length > 1 && (
              <StageViewSwitcher views={views} activeKey={view.key} onSelect={(k) => void selectView(k)} busy={switchingView} />
            )}
            {activeQuestion.hint && showWall && (
              <p
                className="text-rc-stone mt-5 leading-[1.5]"
                style={{ fontSize: "clamp(0.95rem, 1.35vw, 1.2rem)", maxWidth: "70ch" }}
              >
                {activeQuestion.hint}
              </p>
            )}
          </div>

          <div
            key={facilitatedSwapKey}
            className="flex-1 min-h-0 overflow-hidden border-t border-rc-paper/8 pt-6 animate-fade-up"
          >
            {event.responses_paused ? (
              <StagePausedCard />
            ) : showWall ? (
              <ResponseTicker
                responses={visibleResponses}
                question={activeQuestion}
                registerCardRef={registerTickerCardRef}
              />
            ) : (
              <StageResultsPanel
                question={activeQuestion}
                segmentQuestion={segmentQuestion}
                responses={visibleResponses}
                clusters={clusters}
                responsesByCluster={responsesByCluster}
                stats={stats}
                view={currentView}
                quoteIndex={quoteIndex}
                onQuoteStep={canControl ? stepQuote : undefined}
                registerClusterRef={registerClusterCardRef}
                pulseClusterId={pulseClusterId}
                dimmed={rebuildBanner}
                registerCardRef={registerTickerCardRef}
              />
            )}
          </div>
        </div>

        <StageAside
          responseCount={visibleResponses.length}
          participantUrl={participantUrl}
          joinCode={event.join_code}
          incomingLane={
            showLane ? <IncomingLane registerLaneRef={registerLaneRef} /> : null
          }
        />
      </div>

      <InsightTicker
        insights={insights}
        tools={tools}
        breakingToolId={breakingToolId}
        onBreakingConsumed={consumeBreaking}
        leading={
          <StageAIAnchor
            event={event}
            question={activeQuestion}
            responses={visibleResponses}
            tools={tools}
            hasClusters={clusters.length > 0}
          />
        }
        onExpand={() => setInsightOverlayOpen(true)}
      />

      <FlightLayer
        flights={flights}
        onFlightDone={handleFlightDone}
        onFlightLand={handleFlightLand}
      />

      {insightOverlayOpen && (
        <StageInsightOverlay
          slots={tickerSlots}
          questionText={activeQuestion.text}
          onClose={() => setInsightOverlayOpen(false)}
        />
      )}
    </div>
  );
}

// ─── Keyboard ─────────────────────────────────────────────────────────────

/**
 * Cursor-key navigation for the stage carousel. The stage view has no form
 * controls, so we listen on window — but still skip when focus is inside an
 * input/textarea/contenteditable as defense-in-depth.
 *
 *   ← / ↑ / PageUp   → previous phase
 *   → / ↓ / PageDown → next phase
 */
function useStageCarouselKeyboard(advance: () => void, retreat: () => void) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable)
      ) {
        return;
      }
      if (e.key === "ArrowRight" || e.key === "ArrowDown" || e.key === "PageDown") {
        e.preventDefault();
        advance();
      } else if (e.key === "ArrowLeft" || e.key === "ArrowUp" || e.key === "PageUp") {
        e.preventDefault();
        retreat();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [advance, retreat]);
}

/** Digits 1–3 select the n-th stage view for signed-in facilitators. Capture
 *  phase + stopPropagation so nothing underneath also reacts. */
function useStageViewKeyboard(enabled: boolean, views: StageViewDef[], onSelect: (key: StageViewKey) => void) {
  useEffect(() => {
    if (!enabled) return;
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)) return;
      // Never swallow browser chords (Cmd/Ctrl/Alt + digit switches tabs etc.).
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const n = Number(e.key);
      if (!Number.isInteger(n) || n < 1 || n > views.length) return;
      e.preventDefault();
      e.stopPropagation();
      onSelect(views[n - 1].key);
    };
    window.addEventListener("keydown", onKey, { capture: true });
    return () => window.removeEventListener("keydown", onKey, { capture: true });
  }, [enabled, views, onSelect]);
}

// ─── Stage subcomponents ──────────────────────────────────────────────────

function StageHeader({
  eventTitle,
  statusKind,
  progressDots,
}: {
  eventTitle: string;
  statusKind: "live" | "survey";
  progressDots: React.ReactNode;
}) {
  // AI activity now lives in the StageAIAnchor at the bottom-left ticker
  // strip — the header shows the steady room status only.
  const dotCls = statusKind === "live" ? "bg-rc-berry" : "bg-rc-lime";
  const label = statusKind === "live" ? "Live" : "Survey · Live";

  return (
    <header className="relative flex items-center gap-6 px-10 py-5 flex-shrink-0">
      {/* Left: brand wordmark + event title (compact, calm) */}
      <div className="flex items-center gap-4 min-w-0">
        <span className="text-[11px] font-semibold tracking-[0.28em] text-rc-stone uppercase flex-shrink-0">
          READYCAL
        </span>
        <span className="text-rc-paper/15 flex-shrink-0">|</span>
        <span
          className="text-[13px] font-semibold tracking-[-0.005em] text-rc-paper/90 truncate"
          title={eventTitle}
        >
          {truncate(eventTitle, 44)}
        </span>
      </div>

      {/* Centre: question progress (Frage N/M + dots) */}
      {progressDots && (
        <div className="absolute left-1/2 -translate-x-1/2 flex items-center gap-2">
          {progressDots}
        </div>
      )}

      {/* Right: status slot — Live/Survey by default, AI activity while a tool
          is running. Single slot, single signal at a time. */}
      <div
        className="ml-auto inline-flex items-center gap-2 flex-shrink-0 max-w-[280px]"
        role="status"
        aria-live="polite"
      >
        <span className="relative inline-flex w-2 h-2">
          <span className={`absolute inset-0 rounded-full ${dotCls} opacity-70 animate-shimmer`} />
          <span className={`relative inline-flex w-2 h-2 rounded-full ${dotCls}`} />
        </span>
        <span className="text-[11px] font-semibold tracking-[0.22em] text-rc-stone uppercase truncate">
          {label}
        </span>
      </div>
    </header>
  );
}

function StageAside({
  responseCount,
  participantUrl,
  joinCode,
  incomingLane,
}: {
  responseCount: number;
  participantUrl: string;
  joinCode: string;
  incomingLane?: ReactNode;
}) {
  return (
    <aside className="relative z-10 flex-shrink-0 w-64 flex flex-col items-center pt-2 h-full min-h-0">
      <div className="flex flex-col items-center gap-7 flex-shrink-0">
        <div className="text-center">
          <p
            className="font-bold text-rc-paper leading-none tabular-nums tracking-[-0.03em]"
            style={{ fontSize: "clamp(48px, 4.4vw, 72px)" }}
          >
            {responseCount}
          </p>
          <p className="text-[11px] font-semibold tracking-[0.22em] text-rc-stone uppercase mt-2">
            Antworten
          </p>
        </div>

        <div className="bg-rc-paper p-3 rounded-rc-lg shadow-card">
          <QRCode value={participantUrl} size={180} />
        </div>

        <div className="flex flex-col items-center gap-2.5 text-center">
          <p className="text-[10px] font-semibold tracking-[0.22em] text-rc-stone uppercase">
            Scannen oder Code eingeben
          </p>
          <p className="text-[13px] font-mono font-semibold text-rc-paper/85 tracking-[0.04em]">
            {SHORT_JOIN_URL}
          </p>
          <p className="text-rc-lime font-mono font-bold text-[22px] tracking-[0.18em] leading-none">
            {joinCode}
          </p>
        </div>
      </div>

      {incomingLane}
    </aside>
  );
}

function WaitingShell({
  event,
  participantUrl,
  eyebrow,
  eyebrowAccent,
  footerLine,
  action,
}: {
  event: Event;
  participantUrl: string;
  eyebrow: string;
  eyebrowAccent: "berry" | "lime";
  footerLine: string;
  /** Optional control rendered below the footer line (e.g. the start button). */
  action?: ReactNode;
}) {
  const eyebrowCls = eyebrowAccent === "berry" ? "text-rc-berry" : "text-rc-lime";
  const dotCls = eyebrowAccent === "berry" ? "bg-rc-berry" : "bg-rc-lime";

  return (
    <div className="min-h-screen bg-rc-ink text-rc-paper flex flex-col font-sans antialiased relative overflow-hidden">
      <header className="flex items-center gap-4 px-12 py-6 flex-shrink-0">
        <span className="text-[11px] font-semibold tracking-[0.28em] text-rc-stone uppercase">
          READYCAL
        </span>
        <span className="text-rc-paper/15">|</span>
        <span className="inline-flex items-center gap-2">
          <span className="relative inline-flex w-2 h-2">
            <span className={`absolute inset-0 rounded-full ${dotCls} opacity-70 animate-shimmer`} />
            <span className={`relative inline-flex w-2 h-2 rounded-full ${dotCls}`} />
          </span>
          <span className="text-[11px] font-semibold tracking-[0.22em] text-rc-stone uppercase">
            REASONZ
          </span>
        </span>
      </header>

      <img
        src="/brand/stoerer-no-prompting.svg"
        alt=""
        aria-hidden
        className="hidden md:block absolute top-16 right-12 w-[200px] lg:w-[240px] pointer-events-none select-none z-10"
        style={{ transform: "rotate(-8deg)", filter: "drop-shadow(6px 6px 0 #000)" }}
      />

      <main className="flex-1 grid lg:grid-cols-[1.15fr_1fr] gap-12 lg:gap-16 px-12 pb-12 items-center">
        <div className="max-w-3xl">
          <p
            className={`text-[12px] font-semibold tracking-[0.28em] uppercase mb-6 ${eyebrowCls}`}
          >
            {eyebrow}
          </p>
          <h1
            className="text-rc-paper font-bold tracking-[-0.03em] leading-[1.02] text-balance"
            style={{ fontSize: "clamp(2.8rem, 6.5vw, 6.25rem)" }}
          >
            {event.title}
          </h1>
          <div className={`mt-7 h-1.5 w-24 rounded-full ${dotCls}`} aria-hidden />
          {event.description && (
            <p className="text-rc-stone text-[18px] sm:text-[20px] leading-[1.55] max-w-2xl mt-7">
              {event.description}
            </p>
          )}
          <div className="inline-flex items-center gap-2 mt-10 text-rc-stone text-[11px] font-semibold tracking-[0.22em] uppercase">
            <span className="relative inline-flex w-1.5 h-1.5">
              <span className={`absolute inset-0 rounded-full ${dotCls} opacity-70 animate-shimmer`} />
              <span className={`relative inline-flex w-1.5 h-1.5 rounded-full ${dotCls}`} />
            </span>
            {footerLine}
          </div>
          {action && <div className="mt-8">{action}</div>}
        </div>

        <aside className="flex flex-col items-center lg:items-start gap-6">
          <div className="bg-rc-paper p-4 rounded-rc-xl shadow-hard">
            <QRCode value={participantUrl} size={240} />
          </div>
          <div className="flex flex-col items-center lg:items-start gap-2.5">
            <p className="text-[11px] font-semibold tracking-[0.22em] text-rc-stone uppercase">
              Scannen oder Code eingeben
            </p>
            <p className="text-[15px] font-mono font-semibold text-rc-paper/85 tracking-[0.04em]">
              {SHORT_JOIN_URL}
            </p>
            <p className="text-rc-lime font-mono font-bold text-[32px] sm:text-[36px] tracking-[0.18em] leading-none">
              {event.join_code}
            </p>
          </div>
        </aside>
      </main>
    </div>
  );
}

function StagePausedCard() {
  return (
    <div className="flex flex-col items-center justify-center h-full gap-6 text-center">
      <div className="w-16 h-16 rounded-full bg-rc-paper/8 border border-rc-paper/15 flex items-center justify-center">
        <div className="w-5 h-5 rounded-full bg-rc-stone/60 animate-shimmer" />
      </div>
      <p
        className="text-rc-stone font-semibold tracking-[-0.01em]"
        style={{ fontSize: "clamp(1.2rem, 2vw, 1.6rem)" }}
      >
        Wir kuratieren gerade die Beiträge…
      </p>
    </div>
  );
}

function WaitingScreen({
  event,
  participantUrl,
  onStart,
}: {
  event: Event;
  participantUrl: string;
  /** Facilitator-only: opens the first question directly from the stage. */
  onStart?: () => Promise<void>;
}) {
  const [starting, setStarting] = useState(false);

  const handleStart = async () => {
    if (!onStart || starting) return;
    setStarting(true);
    try {
      await onStart();
    } finally {
      setStarting(false);
    }
  };

  return (
    <WaitingShell
      event={event}
      participantUrl={participantUrl}
      eyebrow="Bereit zum Start"
      eyebrowAccent="berry"
      footerLine="Die Session startet gleich"
      action={
        onStart ? (
          <button
            type="button"
            onClick={handleStart}
            disabled={starting}
            className="inline-flex items-center gap-3 bg-rc-berry hover:bg-rc-berry-700 disabled:opacity-60 disabled:cursor-not-allowed text-rc-paper font-semibold text-[15px] rounded-pill px-6 py-3 transition-colors active:scale-[0.97] shadow-berry"
          >
            {starting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
            Erste Frage starten
          </button>
        ) : undefined
      }
    />
  );
}

function OverviewScreen({
  event,
  questions,
}: {
  event: Event;
  questions: ReturnType<typeof useEvent>["questions"];
}) {
  return (
    <div className="min-h-screen bg-rc-ink text-rc-paper flex flex-col px-12 pt-10 pb-8 gap-8 font-sans antialiased">
      <div className="flex-shrink-0 flex items-start justify-between gap-6">
        <div>
          <p className="text-[11px] font-semibold tracking-[0.28em] text-rc-berry uppercase mb-5">
            Session-Übersicht
          </p>
          <h1
            className="font-bold tracking-[-0.03em] leading-[1.02] text-balance"
            style={{ fontSize: "clamp(2.6rem, 5.4vw, 5rem)" }}
          >
            Die Stimmen.
          </h1>
          <p className="text-rc-stone mt-3 text-[18px] sm:text-[20px]">{event.title}</p>
        </div>
        <span className="text-[11px] font-semibold tracking-[0.28em] text-rc-stone uppercase mt-2 flex-shrink-0">
          READYCAL
        </span>
      </div>
      <div className="flex flex-col gap-4 flex-1 overflow-y-auto">
        {questions.map((q) => (
          <QuestionSummary key={q.id} question={q} />
        ))}
      </div>
    </div>
  );
}

function QuestionSummary({
  question,
}: {
  question: ReturnType<typeof useEvent>["questions"][number];
}) {
  const { responses } = useResponses(question.id);
  const { clusters, insights } = useClusters(question.id);
  const visible = responses.filter((r) => isVisibleResponse(r));

  return (
    <article className="bg-rc-graphite rounded-rc-lg p-7 border border-rc-paper/10 shadow-card">
      <p className="text-[10px] font-semibold tracking-[0.22em] text-rc-berry uppercase mb-2">
        Frage {question.order_index}
      </p>
      <p className="text-2xl font-bold text-rc-paper mb-5 tracking-[-0.015em]">{question.text}</p>
      <div className="flex gap-8 text-sm text-rc-stone mb-5 font-medium tabular-nums">
        <span>{visible.length} Antworten</span>
        <span>{clusters.length} Cluster</span>
        <span>{insights.length} Insights</span>
      </div>
      {clusters.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {clusters.map((c) => (
            <span
              key={c.id}
              className="bg-rc-berry/12 text-rc-berry border border-rc-berry/30 rounded-pill px-3 py-1 text-sm font-semibold"
            >
              {c.label} <span className="text-rc-berry/60 ml-1">· {c.size}</span>
            </span>
          ))}
        </div>
      )}
    </article>
  );
}
